import type { EaiwsContext} from '@easterngraphics/wcf/modules/eaiws';
import type { BrowserClient} from '@sentry/browser';

import { Scope } from '@sentry/browser';

import { AppName } from '../Constants';
import VersionInfo from '../Version';

import { getEaiwsExceptionName } from './SoapError';

import { SoapError } from '@easterngraphics/wcf/modules/eaiws';
import { isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

let sentryClient: BrowserClient | undefined;

// Note: for internal (this file) use only
class ErrorWithoutStack extends Error {
    public constructor(error: Error) {
        super(error.message);

        Object.setPrototypeOf(this, Error.prototype);

        // Remove stack since it is not that useful when reporting ofml errors
        this.stack = undefined;
    }
}

const versionCache = new Map<string, string | undefined>();
async function getEaiwsVersion(eaiwsSession?: EaiwsContext): Promise<string | undefined> {
    if (eaiwsSession == null) {
        return undefined;
    }

    const value: string | undefined = versionCache.get(eaiwsSession.baseUrl);
    if (isNotNullOrEmpty(value)) {
        return value;
    }

    const appVersion: string | undefined = (await eaiwsSession.session.getSystemInformation())?.application?.appVersion;
    versionCache.set(eaiwsSession.baseUrl, appVersion);
    return appVersion;
}

const breadcrumbScope: Scope = new Scope();

export function clearDataErrorBreadcrumbs(): void {
    breadcrumbScope.clearBreadcrumbs();
}

async function getScope(eaiwsSession?: EaiwsContext): Promise<Scope> {
    const scope: Scope = new Scope();

    scope.setTags({
        app_name: AppName,
        app_version: VersionInfo?.version,
        eaiws_server: eaiwsSession?.baseUrl,
        eaiws_version: await getEaiwsVersion(eaiwsSession),
    });

    return scope;
}

const contextMap = new Map<string, (error: Error) => Record<string, string> | undefined>();
export function registerDataErrorContext(key: string, callback: (error: Error) => Record<string, string> | undefined): void {
    contextMap.set(key, callback);
}

const ErrorTags: ReadonlyArray<string> = ['manufacturer', 'manufacturerId', 'seriesId'];
export async function captureDataException(error: Error, eaiwsSession: EaiwsContext | undefined): Promise<void> {
    if (sentryClient == null) {
        return;
    }

    const scope: Scope = await getScope(eaiwsSession);
    for (const [contextName, contextGenerator] of contextMap.entries()) {
        const context: Record<string, string> | undefined = contextGenerator(error);
        if (context != null) {
            scope.setContext(contextName, context);

            for (const tagName of ErrorTags) {
                const value: string | undefined = context[tagName];
                if (isNotNullOrEmpty(value)) {
                    scope.setTag(tagName, value);
                }
            }
        }
    }

    sentryClient.captureException(new ErrorWithoutStack(error), {captureContext: scope}, breadcrumbScope);
}

const DataErrorRelatedExceptions = new Set<string>([
    'egr.eai.basket.ArticleDataNotFoundException',
    'egr.eai.basket.ArticleInstantiationException',
    'egr.eai.basket.ArticleMigrationRequiredException', // could pass?
    'egr.eai.basket.InconsistentStateException',
    'egr.eai.fapi.FApiException', // ?? unsure -> could be anything
    'egr.eai.ofml.ContextSelectionException',
    'egr.eai.ofml.InvalidOFMLResultException',
    'egr.eai.ofml.NoInsertPositionFoundException',
    'egr.eai.ofml.NoSuchInterfaceException',
    'egr.eai.ofml.oap.OAPException',
    'egr.eai.ofml.NoOFMLClassFoundException',
    'egr.eai.product.InvalidFinalArticleSpecException',
    'egr.eai.product.InvalidVariantCodeException',
    'egr.eai.product.ProductException',
    'egr.eai.product.PropertyException',
    'egr.eai.product.PropertyStoreException',
    'egr.eai.product.PropertyValueException',
    'egr.eai.product.ReadOnlyPropertyException',
    'egr.eai.product.UnknownPropertyException',
    'java.text.ParseException', // invalid package id?
]);

interface dataError {
    insertDataError: boolean,
    helperText?: string,
}
export function isInsertDataError(error: SoapError | Error): dataError {
    if (error instanceof SoapError) {
        const exceptionName: string | undefined = getEaiwsExceptionName(error);
        if (exceptionName != null) {
            if (DataErrorRelatedExceptions.has(exceptionName)) {
                return { insertDataError: true, helperText: getDataErrorHelperText(error) };
            }
        }
    }

    return { insertDataError: false };
}

const DataErrorExceptions = new Set<string>([
    'egr.eai.ofml.ContextSelectionException',
    'egr.eai.ofml.InvalidOFMLResultException',
    'egr.eai.ofml.NoInsertPositionFoundException',
    'egr.eai.ofml.NoSuchInterfaceException',
    'egr.eai.ofml.oap.OAPException',
    'egr.eai.ofml.NoOFMLClassFoundException',
    'egr.eai.product.InvalidFinalArticleSpecException',
    'egr.eai.product.InvalidVariantCodeException',
    'egr.eai.product.ProductException',
    'egr.eai.product.PropertyException',
    'egr.eai.product.PropertyStoreException',
    'egr.eai.product.PropertyValueException',
    'egr.eai.product.ReadOnlyPropertyException',
    'egr.eai.product.UnknownPropertyException',
    'egr.eai.fapi.FApiException', // ?? unsure -> could be anything
    'java.text.ParseException', // invalid package id?
]);

export function isArticleDataError(error: SoapError | Error): boolean {
    if (error instanceof SoapError) {
        if ((error.message.includes('no catalog found containing') && error.message.includes('and also matching catalogId=')) ||
            (error.message.includes('update state is Invalid')) ||
            (error.message.includes('egr.eai.basket.ArticleDataNotFoundException: article lookup failed')) ||
            (error.message.includes('egr.eai.basket.ArticleDataNotFoundException') && error.message.includes('no ofml class'))
        ) {
            return true;
        }
        const exceptionName: string | undefined = getEaiwsExceptionName(error);
        if (exceptionName != null) {
            return DataErrorExceptions.has(exceptionName);
        }
    }
    return false;
}

export function getDataErrorHelperText(error: SoapError | Error): string | undefined {
    if (error.message.includes('egr.eai.ofml.InvalidOFMLResultException') &&
        error.message.includes('caused by: java.text.ParseException: not a valid unit of measure: 1'
    )){
            return 'Hint: Order unit of OCD article master has invalid value.';
    }
    return undefined;
}